import { createTag, TASK_CACHE_TAGS } from '@frontend/api-client/utils';
import { camelCaseApiResponse } from '@frontend/api-client/utils/transformers';
import type { BaseApi } from '@frontend/api-client/utils/types';
import type {
  CreateDraftTaskPayload,
  CreateDraftTaskResponse,
  CreateTaskFromDraftTaskPayload,
  CreateTaskFromDraftTaskResponse,
  DeleteDraftTaskPayload,
  DeleteDraftTaskResponse,
  EditDraftTaskAfterFailurePayload,
  EditDraftTaskAfterFailureResponse,
  GenerateDraftTaskTimeSlotsPayload,
  GenerateDraftTaskTimeSlotsResponse,
  ListDraftTasksPayload,
  ListDraftTasksResponse,
  RetrieveDraftTaskPayload,
  RetrieveDraftTaskResponse,
  SubmitDraftTaskAnswersPayload,
  SubmitDraftTaskAnswersResponse,
  UpdateDraftTaskPayload,
  UpdateDraftTaskResponse,
} from '@frontend/api-types';
import { convertArrayObjectKeysCase, convertObjectKeysCase } from '@frontend/duck-tape';
import DRAFT_TASKS_ENDPOINTS from './endpoint';

export const getDraftTasksApi = (baseApi: BaseApi) => {
  const draftTasksApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
      createDraftTask: builder.mutation<CreateDraftTaskResponse, CreateDraftTaskPayload>({
        invalidatesTags: [createTag({ id: 'LIST', type: 'DraftTask' })],
        query: ({ formData: body }) => ({
          body,
          method: 'POST',
          url: DRAFT_TASKS_ENDPOINTS.createDraftTask(),
        }),
        transformResponse: camelCaseApiResponse<CreateDraftTaskResponse>,
      }),
      createTaskFromDraftTask: builder.mutation<CreateTaskFromDraftTaskResponse, CreateTaskFromDraftTaskPayload>({
        invalidatesTags: TASK_CACHE_TAGS.map(createTag),
        query: ({ draftTaskId, ...payload }) => ({
          body: convertObjectKeysCase(payload, 'snakeCase'),
          method: 'POST',
          url: DRAFT_TASKS_ENDPOINTS.createTaskFromDraftTask(draftTaskId),
        }),
        transformResponse: camelCaseApiResponse<CreateTaskFromDraftTaskResponse>,
      }),
      deleteDraftTask: builder.mutation<DeleteDraftTaskResponse, DeleteDraftTaskPayload>({
        invalidatesTags: (_result, _error, { draftTaskId }) => [
          createTag({ id: 'LIST', type: 'DraftTask' }),
          createTag({ id: draftTaskId, prefix: 'draftTaskId', type: 'DraftTask' }),
        ],
        onQueryStarted: async ({ draftTaskId: deletedDraftTaskId }, { dispatch }) => {
          try {
            dispatch(
              draftTasksApi.util.updateQueryData('listDraftTasks', undefined, (draftTasks) =>
                draftTasks.filter((draftTask) => draftTask.id !== deletedDraftTaskId),
              ),
            );
          } catch {
            /* If there is an error, don't update the cache */
          }
        },
        query: ({ draftTaskId }) => ({
          method: 'DELETE',
          url: DRAFT_TASKS_ENDPOINTS.deleteDraftTask(draftTaskId),
        }),
      }),
      editDraftTaskAfterFailure: builder.mutation<EditDraftTaskAfterFailureResponse, EditDraftTaskAfterFailurePayload>({
        invalidatesTags: (_result, _error, { draftTaskId }) => [
          createTag({ id: draftTaskId, prefix: 'draftTaskId', type: 'DraftTask' }),
          createTag({ id: 'LIST', type: 'DraftTask' }),
        ],
        query: ({ draftTaskId }) => ({
          method: 'POST',
          url: DRAFT_TASKS_ENDPOINTS.editAfterFailure(draftTaskId),
        }),
      }),
      generateDraftTaskTimeSlots: builder.mutation<
        GenerateDraftTaskTimeSlotsResponse,
        GenerateDraftTaskTimeSlotsPayload
      >({
        query: ({ draftTaskId }) => ({
          method: 'POST',
          url: DRAFT_TASKS_ENDPOINTS.generateSlots(draftTaskId),
        }),
        transformResponse: camelCaseApiResponse<GenerateDraftTaskTimeSlotsResponse>,
      }),
      listDraftTasks: builder.query<ListDraftTasksResponse, ListDraftTasksPayload>({
        providesTags: [createTag({ id: 'LIST', type: 'DraftTask' })],
        query: DRAFT_TASKS_ENDPOINTS.listDraftTasks,
        transformResponse: (response: GenericRecord[]) =>
          convertArrayObjectKeysCase(response, 'camelCase') as ListDraftTasksResponse,
      }),
      retrieveDraftTask: builder.query<RetrieveDraftTaskResponse, RetrieveDraftTaskPayload>({
        providesTags: (_result, _error, { draftTaskId }) => [
          createTag({ id: draftTaskId, prefix: 'draftTaskId', type: 'DraftTask' }),
        ],
        query: ({ draftTaskId }) => ({
          method: 'GET',
          url: DRAFT_TASKS_ENDPOINTS.retrieveDraftTask(draftTaskId),
        }),
        transformResponse: camelCaseApiResponse<RetrieveDraftTaskResponse>,
      }),
      submitDraftTaskIQAnswers: builder.mutation<SubmitDraftTaskAnswersResponse, SubmitDraftTaskAnswersPayload>({
        invalidatesTags: TASK_CACHE_TAGS.map(createTag),
        query: ({ draftTaskId, ...data }) => ({
          body: convertObjectKeysCase(data, 'snakeCase'),
          method: 'PATCH',
          url: DRAFT_TASKS_ENDPOINTS.submitIQAnswers(draftTaskId),
        }),
        transformResponse: camelCaseApiResponse<SubmitDraftTaskAnswersResponse>,
      }),
      updateDraftTask: builder.mutation<UpdateDraftTaskResponse, UpdateDraftTaskPayload>({
        invalidatesTags: (_result, _error, { draftTaskId }) => [
          createTag({ id: draftTaskId, prefix: 'draftTaskId', type: 'DraftTask' }),
          createTag({ id: 'LIST', type: 'DraftTask' }),
        ],
        query: ({ draftTaskId, formData: body }) => ({
          body,
          method: 'PATCH',
          url: DRAFT_TASKS_ENDPOINTS.updateDraftTask(draftTaskId),
        }),
        transformResponse: camelCaseApiResponse<UpdateDraftTaskResponse>,
      }),
    }),
    overrideExisting: true,
  });

  return draftTasksApi;
};
